import React from "react";
import { useColors } from "../constants/Colors";

interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  maxWidth?: number;
  fullWidth?: boolean;
}

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  maxWidth = 360,
  fullWidth = false,
}: SegmentedControlProps<T>) {
  const { accentColor, textNeutralDefault, containerNeutralLightLowest } =
    useColors();

  const selectedIndex = options.findIndex((o) => o.value === value);

  return (
    <div
      className="relative flex items-center overflow-hidden"
      style={{
        backgroundColor: containerNeutralLightLowest,
        borderRadius: 100,
        height: 48,
        padding: 3,
        ...(fullWidth ? { width: "100%" } : { maxWidth, margin: "0 auto" }),
      }}
    >
      {/* Sliding indicator */}
      <div
        className="absolute top-[3px] bottom-[3px] transition-[left] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
        style={{
          width: `calc((100% - 6px) / ${options.length})`,
          left: `calc(3px + ${selectedIndex} * (100% - 6px) / ${options.length})`,
          backgroundColor: accentColor,
          borderRadius: 96,
        }}
      />
      {/* Options */}
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className="relative z-10 flex-1 h-full flex items-center justify-center text-sm font-medium transition-colors duration-200"
          style={{
            color: value === option.value ? "#fff" : textNeutralDefault,
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export default SegmentedControl;
